import { computed, Ref, ref, watch } from 'vue'
import { QueryObserverResult, useQueries, useQueryClient } from '@tanstack/vue-query'
import { AxiosError, AxiosResponse } from 'axios'
import { filterJobsBucketCounts, BucketCountsResponse } from '@/api/jobs'
import upperFirst from 'lodash/upperFirst'
import { prepareFiltersForFetch, serializeFiltersForQueryKey } from './useFilterJobsQuery'
import {
  BucketDefinition,
  getBucketDateFilterId,
  isBucketDateFilter,
  isBucketStatusFilter,
} from './useJobFilterBuckets'
import type { JobFilter } from '@ironsight/types'
import { getDefaultDateRangeForBucket } from './bucketDateRangeUtils'
import { createRefetchIntervalFn } from './createRefetchInterval'
import { currentTimezone } from '@/utils/date-formatting'

export const BUCKET_COUNT_QUERY_KEY = 'bucket_counts'

const BUCKET_COUNT_STALE_TIME = 60_000
const REFETCH_INTERVAL = 60_000

type BucketGroupId = BucketDefinition['group']

function getFiltersForBucketCount(filters: JobFilter[], bucket: BucketDefinition): JobFilter[] {
  const baseFilters = filters.filter(
    (filter) => !isBucketDateFilter(filter) && !isBucketStatusFilter(filter),
  )
  const bucketDateFilter = filters.find(
    (filter) => isBucketDateFilter(filter) && filter.id === getBucketDateFilterId(bucket.id),
  )

  if (bucketDateFilter) {
    return [...baseFilters, bucketDateFilter]
  }

  const dateRange = getDefaultDateRangeForBucket(bucket)
  const config = bucket.dateRangeFilter
  if (!dateRange || !config) return baseFilters

  return [
    ...baseFilters,
    {
      id: `bucket_count_date_${bucket.id}`,
      property: config.property,
      type: 'DateTime',
      comparison: 'Between',
      value: dateRange,
      hidden: true,
    },
  ]
}

/**
 * Fetches and manages job bucket counts.
 *
 * Refetches all buckets when any filters or bucket definitions change to keep
 * the polling intervals intact.
 */
export function useJobBucketCounts(
  bucketDefinitions: Ref<readonly BucketDefinition[]>,
  filters: Ref<JobFilter[]>,
  options?: {
    enabled?: Ref<boolean>
    excludeGroups?: Ref<BucketGroupId[]>
  },
) {
  const enabled = computed(() => options?.enabled?.value ?? true)
  const excludeGroups = computed(() => options?.excludeGroups?.value ?? [])
  const queryClient = useQueryClient()
  const mergedCounts = ref<Record<string, number>>({})
  const querySnapshot = ref<QueryObserverResult<AxiosResponse<BucketCountsResponse>, AxiosError>[]>(
    [],
  )
  const refreshError = ref(false)
  const bucketCountsRefetchInterval = createRefetchIntervalFn<BucketCountsResponse>(
    REFETCH_INTERVAL,
    refreshError,
  )

  const previousBucketGroupRequestKeys = ref<Map<BucketGroupId, string> | null>(null)
  const bucketGroupMap = computed(() => {
    const map = new Map<string, BucketGroupId>()
    for (const bucket of bucketDefinitions.value) {
      map.set(bucket.id, bucket.group)
    }
    return map
  })

  const groupedBuckets = computed(() => {
    const groups = new Map<BucketGroupId, BucketDefinition[]>()
    const excluded = excludeGroups.value

    for (const bucket of bucketDefinitions.value) {
      if (excluded.includes(bucket.group)) continue

      const groupBuckets = groups.get(bucket.group)
      if (groupBuckets) {
        groupBuckets.push(bucket)
      } else {
        groups.set(bucket.group, [bucket])
      }
    }
    return Array.from(groups, ([group, buckets]) => ({ group, buckets }))
  })

  const buildBucketRequests = (buckets: BucketDefinition[]) =>
    buckets.map((bucket) => ({
      bucket_id: upperFirst(bucket.id),
      filters: prepareFiltersForFetch(getFiltersForBucketCount(filters.value, bucket)),
    }))

  const buildBucketGroupRequestKey = (buckets: BucketDefinition[]) => {
    const stableBuckets = [...buckets].sort((a, b) => a.id.localeCompare(b.id))
    return JSON.stringify(
      stableBuckets.map((bucket) => ({
        bucketId: bucket.id,
        filters: serializeFiltersForQueryKey(getFiltersForBucketCount(filters.value, bucket)),
        timeZone: currentTimezone(),
      })),
    )
  }

  const bucketGroupRequestKeys = computed(() =>
    groupedBuckets.value.map((group) => ({
      group: group.group,
      key: buildBucketGroupRequestKey(group.buckets),
    })),
  )

  const bucketGroupQueries = computed(() =>
    groupedBuckets.value.map((group) => {
      const sortedBucketIds = group.buckets.map((bucket) => bucket.id).sort()

      return {
        group: group.group,
        query: {
          queryKey: [BUCKET_COUNT_QUERY_KEY, group.group, sortedBucketIds],
          queryFn: async (context) => {
            const requests = buildBucketRequests(group.buckets)
            if (requests.length === 0) {
              return { data: {} } as AxiosResponse<BucketCountsResponse>
            }
            const response = await filterJobsBucketCounts(
              requests,
              currentTimezone(),
              context.signal,
            )
            return response
          },
          enabled: enabled.value && group.buckets.length > 0,
          refetchInterval: (data, query) =>
            enabled.value ? bucketCountsRefetchInterval(data, query) : false,
          refetchOnWindowFocus: true,
          staleTime: BUCKET_COUNT_STALE_TIME,
          keepPreviousData: true,
          retry: 2,
        },
      }
    }),
  )

  const queryResults = useQueries({
    queries: computed(() => bucketGroupQueries.value.map((group) => group.query)),
  }) as unknown as ReadonlyArray<
    QueryObserverResult<AxiosResponse<BucketCountsResponse>, AxiosError>
  >

  const groupStatusMap = computed(() => {
    const map = new Map<
      BucketGroupId,
      { isFetching: boolean; isLoading: boolean; isError: boolean }
    >()

    bucketGroupQueries.value.forEach((group, index) => {
      const result = querySnapshot.value[index]
      map.set(group.group, {
        isFetching: result?.isFetching ?? false,
        isLoading: result?.isLoading ?? false,
        isError: result?.isError ?? false,
      })
    })

    return map
  })

  const bucketCounts = computed(() => mergedCounts.value)

  watch(
    bucketGroupRequestKeys,
    (currentGroups) => {
      const previousGroups = previousBucketGroupRequestKeys.value
      const currentGroupMap = new Map(currentGroups.map((group) => [group.group, group.key]))

      if (previousGroups) {
        for (const group of currentGroups) {
          if (previousGroups.get(group.group) === group.key) {
            continue
          }

          queryClient.cancelQueries({ queryKey: [BUCKET_COUNT_QUERY_KEY, group.group] })
          queryClient.invalidateQueries({
            queryKey: [BUCKET_COUNT_QUERY_KEY, group.group],
            refetchType: 'active',
          })
        }
      }

      previousBucketGroupRequestKeys.value = currentGroupMap
    },
    { deep: true, immediate: true },
  )

  watch(
    queryResults,
    (newQueries) => {
      querySnapshot.value = [...newQueries]
    },
    { deep: true, immediate: true },
  )

  watch(
    [bucketGroupQueries, querySnapshot],
    ([groups, snapshot]) => {
      let updatedCounts = mergedCounts.value
      let hasData = false

      groups.forEach((_group, index) => {
        const result = snapshot[index]
        const response = result?.data
        if (!response?.data) return
        updatedCounts = { ...updatedCounts, ...response.data }
        hasData = true
      })

      if (!hasData) return
      mergedCounts.value = updatedCounts
    },
    { immediate: true },
  )

  const getBucketCount = (bucketId: string): number | null | undefined => {
    return bucketCounts.value[bucketId.toLowerCase()]
  }

  const getDisplayCount = (bucketId: string): string | undefined => {
    const count = getBucketCount(bucketId)
    if (count === null || count === undefined) return undefined
    return count.toString()
  }

  const isBucketFetching = (bucketId: string): boolean => {
    const isKnownBucket = bucketDefinitions.value.some((b) => b.id === bucketId)
    if (!isKnownBucket) return false
    const bucketGroup = bucketGroupMap.value.get(bucketId)
    if (bucketGroup === undefined) return false
    const status = groupStatusMap.value.get(bucketGroup)
    return status?.isFetching ?? false
  }

  const shouldShowBucketCount = (bucketId: string): boolean => {
    const isKnownBucket = bucketDefinitions.value.some((b) => b.id === bucketId)
    if (!isKnownBucket) return false
    const bucketGroup = bucketGroupMap.value.get(bucketId)
    if (bucketGroup === undefined) return false
    // Don't show count for excluded groups
    if (excludeGroups.value.includes(bucketGroup)) return false
    const status = groupStatusMap.value.get(bucketGroup)
    if (status?.isError) return false
    return true
  }

  return {
    bucketCounts,
    isBucketFetching,
    getBucketCount,
    getDisplayCount,
    shouldShowBucketCount,
  }
}
